import { t } from 'i18next'
import { ColDef, ValueFormatterParams, ISetFilterParams } from 'ag-grid-enterprise'
import { Voucher } from '../../../../pages/reporting/vouchers/types'
import { parseDate } from '../../../../config/dayjsConfig'
import { formatValueByType } from '../../../../utils/helpers'
import { Customer } from '../../../../types/customer/customer'
import { Company } from '../../../../types/company/Company'
import VoucherActionsCellRenderer from './VoucherActionsCellRenderer'
import DateRangeColumnFilter from '../../../../shared/components/custom/Grid/Filter/DateRangeColumnFilter'

interface GetColumnsParams {
  voucherTypes: string[]
  customers: Customer[]
  companies?: Company[]
  showActions: boolean
  showCompanyColumn?: boolean
  showReferenceNumber?: boolean
}

const dateValueFormatter = (params: ValueFormatterParams): string => {
  const date = params.value
  return date ? parseDate(date)?.format('DD.MM.YYYY') || '' : ''
}

const currencyValueFormatter = (params: ValueFormatterParams): string => {
  return formatValueByType(params.value, undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  })
}

export const getVoucherTableColumns = ({
  voucherTypes,
  customers,
  companies = [],
  showActions,
  showCompanyColumn = false,
  showReferenceNumber = false
}: GetColumnsParams): ColDef<Voucher>[] => {
  const columns: ColDef<Voucher>[] = [
    {
      headerName: t('vouchersPage:voucherGuid'),
      field: 'guid',
      width: 150,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['reset', 'apply']
      },
      cellRenderer: 'agGroupCellRenderer'
    },
    {
      headerName: t('vouchersPage:voucherType'),
      field: 'type',
      width: 150,
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: voucherTypes,
        buttons: ['reset', 'apply']
      } as ISetFilterParams,
      tooltipField: 'type'
    },
    {
      headerName: t('global:company'),
      field: 'companyId',
      width: 200,
      sortable: true,
      hide: !showCompanyColumn,
      valueFormatter: (params: ValueFormatterParams) => {
        const company = companies.find(c => c.id === params.value)
        return company ? company.name : params.value || ''
      },
      filter: 'agSetColumnFilter',
      filterParams: {
        values: companies.map(c => c.id),
        valueFormatter: (params: ValueFormatterParams) => {
          const company = companies.find(c => c.id === params.value)
          return company ? company.name : params.value || ''
        },
        buttons: ['reset', 'apply']
      } as ISetFilterParams
    },
    {
      headerName: t('vouchersPage:invoicePartnerName'),
      field: 'invoice.partner.name',
      colId: 'invoicePartnerId',
      width: 150,
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: customers.map(c => c.id),
        valueFormatter: (params: ValueFormatterParams) => {
          const customer = customers.find(c => c.id === params.value)
          return customer?.name || params.value
        },
        buttons: ['reset', 'apply']
      } as ISetFilterParams,
      valueGetter: params => params.data?.invoice?.partner?.name
    },
    {
      headerName: 'Debet',
      field: 'debit',
      width: 130,
      type: 'numericColumn',
      sortable: false,
      valueFormatter: currencyValueFormatter
    },
    {
      headerName: 'Kredit',
      field: 'credit',
      width: 130,
      type: 'numericColumn',
      sortable: false,
      valueFormatter: currencyValueFormatter
    },
    {
      headerName: t('vouchersPage:voucherStatus'),
      field: 'status',
      width: 110,
      sortable: true
    },
    {
      headerName: t('vouchersPage:voucherDate'),
      field: 'date',
      colId: 'date',
      width: 150,
      sortable: true,
      filter: DateRangeColumnFilter,
      valueFormatter: dateValueFormatter
    },
    {
      headerName: t('vouchersPage:invoiceNumber'),
      field: 'invoice.number',
      colId: 'invoiceNumber',
      width: 150,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['reset', 'apply']
      },
      valueGetter: params => params.data?.invoice?.number
    },
    {
      headerName: t('vouchersPage:invoiceReference'),
      field: 'invoice.referenceNumber',
      colId: 'referenceNumber',
      width: 150,
      sortable: true,
      hide: !showReferenceNumber,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['reset', 'apply']
      },
      valueGetter: params => params.data?.invoice?.referenceNumber
    },
    {
      headerName: t('vouchersPage:invoicePaymentDate'),
      field: 'invoice.paymentDate',
      width: 150,
      sortable: true,
      valueGetter: params => params.data?.invoice?.paymentDate,
      valueFormatter: dateValueFormatter
    },
    {
      headerName: t('vouchersPage:voucherDescription'),
      field: 'description',
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['reset', 'apply']
      },
      tooltipField: 'description'
    }
  ]

  if (showActions) {
    columns.push({
      headerName: t('global:actions'),
      colId: 'actions',
      width: 100,
      pinned: 'right',
      sortable: false,
      filter: false,
      cellRenderer: VoucherActionsCellRenderer
    })
  }

  return columns
}
